/**
 * Returns the customer to the page they came from after a prescription lens
 * configuration is added to the cart.
 *
 * Query parameters:
 *   return_to  same-origin path to return to after a successful add
 *   edit_line  cart line key to remove after the new configuration is added
 *
 * Also suppresses the cart drawer/notification for this page so the redirect
 * is not interrupted by a drawer opening.
 */
(function () {
  const params = new URLSearchParams(window.location.search);
  const returnTo = sanitizeReturnTo(params.get('return_to'));
  const editLine = params.get('edit_line');
  const events =
    typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : { cartUpdate: 'cart-update' };

  if (!returnTo || typeof subscribe !== 'function') return;
  if (!document.querySelector('form[data-type="add-to-cart-form"]')) return;
  // Only the prescription lens PDP renders this block; other product pages can
  // legitimately carry ?return_to= for unrelated apps.
  if (!document.querySelector('.select-lens-section')) return;

  document.body.setAttribute('data-suppress-cart-open', '');

  // /cart/add.js merges into the existing line when the variant and properties
  // are unchanged. Record the old quantity up front so removeLine() can tell
  // "a new line was created" (delete the old one) from "the old line absorbed
  // the add" (only decrement it, otherwise the configured item is lost).
  const originalQuantityPromise = editLine ? fetchLineQuantity(editLine) : Promise.resolve(null);

  subscribe(events.cartUpdate, async (event) => {
    if (event?.source !== 'product-form') return;
    if (!isCurrentProductVariant(event.productVariantId)) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'bundle_lens_add',
      edit_existing: Boolean(editLine),
    });

    if (editLine) {
      await removeLine(editLine, await originalQuantityPromise);
    }

    window.location.assign(returnTo);
  });

  function sanitizeReturnTo(value) {
    if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
      return null;
    }

    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      if (url.pathname === window.location.pathname && !params.get('edit_line')) return null;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
      return null;
    }
  }

  function isCurrentProductVariant(variantId) {
    const formIds = Array.from(
      document.querySelectorAll('form[data-type="add-to-cart-form"] input[name="id"]')
    )
      .map((input) => input.value)
      .filter(Boolean);

    return formIds.length === 0 || formIds.includes(String(variantId));
  }

  async function removeLine(lineKey, originalQuantity) {
    const changeUrl = window.routes?.cart_change_url || '/cart/change';

    // Without the pre-add quantity we cannot know whether the new configuration
    // merged into the old line. Leave both lines rather than risk deleting the
    // item the customer just configured; the cart page can remove the extra.
    if (originalQuantity === null) return;

    const cart = await getCart();
    if (!cart) return;

    const line = (cart.items || []).find((item) => item.key === lineKey);
    if (!line) return;

    const merged = line.quantity > originalQuantity;
    const quantity = merged ? originalQuantity : 0;

    try {
      await fetch(`${changeUrl}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
        body: new URLSearchParams({ id: lineKey, quantity: String(quantity) }),
      });
    } catch (error) {
      // The redirect still proceeds; the old line can be removed from the cart.
    }
  }

  function cartUrl() {
    return window.routes?.cart_url || '/cart';
  }

  async function getCart() {
    try {
      const response = await fetch(`${cartUrl()}.js`, {
        headers: { Accept: 'application/json' },
      });
      return response.ok ? response.json() : null;
    } catch (error) {
      return null;
    }
  }

  async function fetchLineQuantity(lineKey) {
    const cart = await getCart();
    if (!cart) return null;

    const line = (cart.items || []).find((item) => item.key === lineKey);
    return line ? line.quantity : null;
  }
})();
